import React, { FormEvent, useEffect, useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import { FormTextField } from "../../../../common/dashboard/FormFields";
import { commonStyles } from "../../../../common/commonStyles";
import PermissionMatrix from "./PermissionMatrix";
import {
  isStaffUserFormDirty,
  normalizeStaffUserFormValue,
  StaffUserFormErrors,
  validateStaffUserForm,
} from "./staffUserValidation";
import { StaffUserFormValue } from "./types";

// CMP Dashboard (24) CD:9883: the employee pop-up intro, the same text for
// Create Employees and Permissions and Update Employee Permissions.
export const EMPLOYEE_FORM_INTRO_TEXT =
  "Create individual employee accounts and assign permissions to control access to specific areas of the Chamber Member Pro™ Dashboard and Website Builder. Reset passwords, update privileges, or remove access at any time.";

export type StaffUserFormProps = {
  mode: "create" | "update";
  initialValue: StaffUserFormValue;
  onSubmit: (value: StaffUserFormValue) => Promise<void>;
  onCancel?: () => void;
  cancelPath?: string;
  onDirtyChange?: (isDirty: boolean) => void;
  loading?: boolean;
  submitError?: string;
};

const cloneValue = (value: StaffUserFormValue): StaffUserFormValue => ({
  ...value,
  permissions: {
    chamberMemberPro: [...value.permissions.chamberMemberPro],
    chamberPerksAppAdminConsole: [
      ...value.permissions.chamberPerksAppAdminConsole,
    ],
  },
});

export const StaffUserForm = ({
  mode,
  initialValue,
  onSubmit,
  onCancel,
  cancelPath,
  onDirtyChange,
  loading = false,
  submitError,
}: StaffUserFormProps) => {
  const [value, setValue] = useState<StaffUserFormValue>(() =>
    cloneValue(initialValue)
  );
  const [errors, setErrors] = useState<StaffUserFormErrors>({});

  useEffect(() => {
    onDirtyChange?.(
      isStaffUserFormDirty({ currentValue: value, initialValue })
    );
  }, [initialValue, onDirtyChange, value]);

  const updateField = (
    field: "firstName" | "lastName" | "jobTitle" | "email",
    nextValue: string
  ) => {
    setValue((current) => ({ ...current, [field]: nextValue }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationErrors = validateStaffUserForm(value);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    await onSubmit(normalizeStaffUserFormValue(value));
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid grid-cols-1 gap-x-7 md:grid-cols-2">
        <FormTextField
          id={`${mode}-staff-first-name`}
          label="First Name "
          value={value.firstName}
          onChange={(nextValue) => updateField("firstName", nextValue)}
          error={errors.firstName}
          placeholder="Enter First Name"
          required
          disabled={loading}
          maxLength={80}
          containerClassName="mb-5 min-w-0"
        />
        <FormTextField
          id={`${mode}-staff-last-name`}
          label="Last Name "
          value={value.lastName}
          onChange={(nextValue) => updateField("lastName", nextValue)}
          error={errors.lastName}
          placeholder="Enter last name"
          required
          disabled={loading}
          maxLength={80}
          containerClassName="mb-5 min-w-0"
        />
        <FormTextField
          id={`${mode}-staff-job-title`}
          label="Job Title"
          value={value.jobTitle}
          onChange={(nextValue) => updateField("jobTitle", nextValue)}
          error={errors.jobTitle}
          placeholder="Enter Job Title"
          disabled={loading}
          maxLength={100}
          containerClassName="mb-5 min-w-0"
        />
        <FormTextField
          id={`${mode}-staff-email`}
          label="Email "
          value={value.email}
          onChange={(nextValue) => updateField("email", nextValue)}
          error={errors.email}
          placeholder="Enter Email Address"
          required
          type="email"
          disabled={loading || mode === "update"}
          helperText={
            mode === "update"
              ? "Email cannot be changed after this account is created."
              : undefined
          }
          maxLength={320}
          containerClassName="mb-5 min-w-0"
        />
      </div>

      <div className="mt-2">
        <div className="mb-3">
          <h4 className="text-base font-extrabold text-gray-950">
            Select Permissions <span className="text-red-500">*</span>
          </h4>
          <p className="mt-1 text-sm leading-5 text-gray-600">
            Choose the areas this employee will be able to access and manage.
          </p>
        </div>
        <PermissionMatrix
          value={value.permissions}
          onChange={(permissions) => {
            setValue((current) => ({ ...current, permissions }));
            setErrors((current) => ({ ...current, permissions: undefined }));
          }}
          disabled={loading}
          error={errors.permissions}
        />
      </div>

      <div className="mt-7">
        {submitError ? (
          <Alert severity="error" className="mb-4">
            {submitError}
          </Alert>
        ) : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          {cancelPath ? (
            <Button
              component={Link}
              to={cancelPath}
              variant="outlined"
              disabled={loading}
              sx={{
                borderColor: "#D7DEE8",
                color: "#111827",
                fontWeight: 800,
                minHeight: 52,
                minWidth: 144,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              type="button"
              variant="outlined"
              disabled={loading}
              onClick={onCancel}
              sx={{
                borderColor: "#D7DEE8",
                color: "#111827",
                fontWeight: 800,
                minHeight: 52,
                minWidth: 144,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={18} />
              ) : undefined
            }
            sx={{
              backgroundColor: commonStyles.cpaGold,
              color: "white",
              fontWeight: 900,
              minHeight: 52,
              minWidth: { sm: mode === "create" ? 300 : 180 },
              textTransform: "none",
              "&:hover": { backgroundColor: commonStyles.cpaGoldDark },
            }}
          >
            {mode === "create"
              ? "Create Employee & Send Credentials"
              : "Save Changes"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default StaffUserForm;
